<template>
  <div
    class="md:hidden flex max-w-[1280px] mx-auto px-4 md:px-6 h-16 items-center justify-between"
  >
    <!-- Logo -->
    <Link :href="navData.rootPath" class="flex items-center gap-2.5 shrink-0">
      <EmmalagatLogo :size="48" class="cursor-pointer" />
    </Link>

    <!-- Search + Hamburger -->
    <div class="flex items-center gap-1">
      <button
        class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
        :aria-label="searchOpen ? 'Stäng sök' : 'Öppna sök'"
        @click="toggleSearch"
      >
        <X v-if="searchOpen" :size="18" class="text-[#3c4947]" />
        <Search v-else :size="18" class="text-[#3c4947]" />
      </button>
      <button
        class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
        aria-label="Toggle menu"
        @click="$emit('toggle-menu')"
      >
        <component
          :is="menuOpen ? XIcon : MenuIcon"
          :size="22"
          class="text-[#191c1e]"
        />
      </button>
    </div>
  </div>

  <!-- Search bar below navbar -->
  <Transition name="slide-down">
    <div
      v-if="searchOpen"
      class="md:hidden border-t border-[#eceef0] bg-white px-6 py-3"
    >
      <input
        ref="searchInputRef"
        v-model="searchQuery"
        type="search"
        placeholder="Sök recept…"
        class="w-full py-2 px-4 text-sm rounded-lg border border-[#eceef0] bg-[#f8fafa] focus:border-teal-400 focus:outline-none focus:ring-0 text-[#191c1e]"
        @keydown.enter="submitSearch"
        @keydown.esc="closeSearch"
      />
    </div>
  </Transition>

  <!-- Mobile Menu Dropdown -->
  <div
    v-if="menuOpen"
    class="md:hidden border-t border-[#eceef0] bg-white shadow-lg"
  >
    <div class="flex flex-col px-6 py-2">
      <Link
        v-for="link in navLinks"
        :key="link.href"
        :href="link.href"
        class="py-4 border-b border-[#f2f4f6] last:border-0 block text-base no-underline"
        :class="
          isActive(link.href)
            ? 'font-bold text-teal-500'
            : 'font-medium text-[#191c1e]'
        "
        @click="$emit('toggle-menu')"
      >
        {{ link.label }}
      </Link>
      <div class="flex gap-3 py-4">
        <Link
          :href="
            navData.currentUser
              ? navData.currentUser.recipesPath
              : navData.newUserPath
          "
          class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors"
        >
          <Heart :size="18" class="text-[#3c4947]" />
        </Link>
        <button
          class="p-2 hover:bg-[#f2f4f6] rounded-full transition-colors cursor-pointer"
          @click="$emit('open-drawer')"
        >
          <User :size="18" class="text-[#3c4947]" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, nextTick } from "vue";
import { Link, router, usePage } from "@inertiajs/vue3";
import EmmalagatLogo from "@/components/icons/EmmalagatLogo.vue";
import {
  Search,
  Heart,
  User,
  Menu as MenuIcon,
  X,
  X as XIcon,
} from "lucide-vue-next";

const props = defineProps({
  navData: { type: Object, required: true },
  menuOpen: { type: Boolean, default: false },
});

defineEmits(["open-drawer", "toggle-menu"]);

const searchOpen = ref(false);
const searchQuery = ref("");
const searchInputRef = ref(null);

async function toggleSearch() {
  searchOpen.value = !searchOpen.value;
  if (searchOpen.value) {
    await nextTick();
    searchInputRef.value?.focus();
  } else {
    searchQuery.value = "";
  }
}

function closeSearch() {
  searchOpen.value = false;
  searchQuery.value = "";
}

function submitSearch() {
  const query = searchQuery.value.trim();
  closeSearch();
  if (!query) {
    router.visit(props.navData.searchPath);
    return;
  }
  router.visit(
    `${props.navData.searchPath}?search=${encodeURIComponent(query)}`,
  );
}

const page = usePage();

const navLinks = computed(() => [
  { label: "Alla recept", href: props.navData.recipesPath },
  { label: "Kategorier", href: props.navData.categoriesPath },
  { label: "Tekniker & verktyg", href: props.navData.toolsPath },
]);

function isActive(href) {
  return page.url === href || page.url.startsWith(href + "/");
}
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition:
    max-height 0.25s ease,
    opacity 0.2s ease;
  max-height: 80px;
  overflow: hidden;
}
.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}
</style>
